/**
 * Enhanced Video Controls
 * Provides progress bar, mute toggle, and modal fullscreen functionality
 * for Shopify native videos with immersive glass morphism UI
 */

(function() {
  'use strict';

  // Utility: Format time in MM:SS
  function formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // Enhanced Video Controls Class
  class EnhancedVideoControls {
    constructor(container) {
      this.container = container;
      this.controls = container.querySelector('[data-video-controls]');
      
      if (!this.controls) return;

      this.video = null;
      this.isReady = false;
      this.isDragging = false;

      // Control elements
      this.progressContainer = this.controls.querySelector('[data-progress-container]');
      this.progressFill = this.controls.querySelector('[data-progress-fill]');
      this.progressInput = this.controls.querySelector('[data-progress-input]');
      this.bufferedBar = this.controls.querySelector('[data-buffered]');
      this.currentTimeEl = this.controls.querySelector('[data-current-time]');
      this.durationEl = this.controls.querySelector('[data-duration]');
      this.muteBtn = this.controls.querySelector('[data-mute-btn]');
      this.fullscreenBtn = this.controls.querySelector('[data-fullscreen-btn]');

      // Find associated modal
      const videoId = container.querySelector('video')?.id || 
                      container.querySelector('template video')?.id ||
                      Math.random().toString(36).substr(2, 9);
      this.modal = document.querySelector(`[data-video-modal="${videoId}"]`) ||
                   container.nextElementSibling?.classList.contains('video-modal') ?
                   container.nextElementSibling : null;

      this.init();
    }

    init() {
      // Wait for video to be loaded
      this.observeVideo();
      this.bindEvents();
    }

    observeVideo() {
      // Check if video already exists
      const existingVideo = this.container.querySelector('video');
      if (existingVideo) {
        this.setupVideo(existingVideo);
        return;
      }

      // Observer for when template content is loaded
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeName === 'VIDEO') {
              this.setupVideo(node);
              observer.disconnect();
              return;
            }
          }
        }
      });

      observer.observe(this.container, { childList: true, subtree: true });
    }

    setupVideo(video) {
      this.video = video;
      this.isReady = true;

      // Initial state
      this.updateMuteState();
      
      // Video events
      video.addEventListener('loadedmetadata', () => this.onMetadataLoaded());
      video.addEventListener('timeupdate', () => this.onTimeUpdate());
      video.addEventListener('progress', () => this.onProgress());
      video.addEventListener('play', () => this.onPlay());
      video.addEventListener('pause', () => this.onPause());
      video.addEventListener('volumechange', () => this.updateMuteState());

      // If metadata already loaded
      if (video.readyState >= 1) {
        this.onMetadataLoaded();
      }
    }

    bindEvents() {
      // Progress bar interaction
      if (this.progressInput) {
        this.progressInput.addEventListener('input', (e) => this.onProgressInput(e));
        this.progressInput.addEventListener('mousedown', () => this.isDragging = true);
        this.progressInput.addEventListener('mouseup', () => this.isDragging = false);
        this.progressInput.addEventListener('touchstart', () => this.isDragging = true, { passive: true });
        this.progressInput.addEventListener('touchend', () => this.isDragging = false);
      }

      // Progress container click
      if (this.progressContainer) {
        this.progressContainer.addEventListener('click', (e) => this.onProgressClick(e));
      }

      // Mute button
      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', (e) => this.toggleMute(e));
      }

      // Fullscreen/Modal button
      if (this.fullscreenBtn) {
        this.fullscreenBtn.addEventListener('click', (e) => this.openModal(e));
      }

      // Modal events
      if (this.modal) {
        const closeButtons = this.modal.querySelectorAll('[data-modal-close]');
        closeButtons.forEach(btn => {
          btn.addEventListener('click', () => this.closeModal());
        });

        // Close on escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
            this.closeModal();
          }
        });
      }

      // Show controls on interaction
      this.controls.addEventListener('mouseenter', () => this.controls.classList.add('is-active'));
      this.controls.addEventListener('mouseleave', () => {
        if (!this.isDragging) {
          this.controls.classList.remove('is-active');
        }
      });
    }

    onMetadataLoaded() {
      if (this.durationEl && this.video) {
        this.durationEl.textContent = formatTime(this.video.duration);
      }
    }

    onTimeUpdate() {
      if (!this.video || this.isDragging) return;

      const progress = (this.video.currentTime / this.video.duration) * 100;
      
      if (this.progressFill) {
        this.progressFill.style.width = `${progress}%`;
      }
      
      if (this.progressInput) {
        this.progressInput.value = progress;
      }

      if (this.progressContainer) {
        this.progressContainer.style.setProperty('--progress', progress);
      }

      if (this.currentTimeEl) {
        this.currentTimeEl.textContent = formatTime(this.video.currentTime);
      }
    }

    onProgress() {
      if (!this.video || !this.bufferedBar) return;

      if (this.video.buffered.length > 0) {
        const bufferedEnd = this.video.buffered.end(this.video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / this.video.duration) * 100;
        this.bufferedBar.style.width = `${bufferedPercent}%`;
      }
    }

    onPlay() {
      this.container.setAttribute('playing', '');
    }

    onPause() {
      this.container.removeAttribute('playing');
    }

    onProgressInput(e) {
      if (!this.video) return;

      const value = parseFloat(e.target.value);
      const time = (value / 100) * this.video.duration;
      
      // Update visual immediately for responsiveness
      if (this.progressFill) {
        this.progressFill.style.width = `${value}%`;
      }
      if (this.progressContainer) {
        this.progressContainer.style.setProperty('--progress', value);
      }

      // Seek video
      this.video.currentTime = time;
    }

    onProgressClick(e) {
      if (!this.video || e.target === this.progressInput) return;

      const rect = this.progressContainer.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percent = (clickX / rect.width) * 100;
      const time = (percent / 100) * this.video.duration;

      this.video.currentTime = time;
    }

    toggleMute(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.video) return;

      this.video.muted = !this.video.muted;
      this.updateMuteState();
    }

    updateMuteState() {
      if (!this.muteBtn || !this.video) return;
      
      this.muteBtn.setAttribute('data-muted', this.video.muted);
      this.muteBtn.setAttribute('aria-label', this.video.muted ? 'Unmute' : 'Mute');
    }

    openModal(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.modal) return;

      // Pause the inline video
      if (this.video) {
        this.video.pause();
      }

      // Open modal
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Play modal video
      const modalVideo = this.modal.querySelector('video');
      if (modalVideo) {
        // Sync time with inline video
        if (this.video) {
          modalVideo.currentTime = this.video.currentTime;
        }
        modalVideo.play().catch(() => {});
      }

      // Focus trap
      this.modal.querySelector('[data-modal-close]')?.focus();
    }

    closeModal() {
      if (!this.modal) return;

      // Pause modal video
      const modalVideo = this.modal.querySelector('video');
      if (modalVideo) {
        modalVideo.pause();
        
        // Sync time back to inline video
        if (this.video) {
          this.video.currentTime = modalVideo.currentTime;
        }
      }

      // Close modal
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Resume inline video
      if (this.video) {
        this.video.play().catch(() => {});
      }

      // Return focus
      this.fullscreenBtn?.focus();
    }
  }

  // Initialize on DOM ready
  function initEnhancedControls() {
    const videoContainers = document.querySelectorAll('[data-enhanced-controls], .has-enhanced-controls');
    videoContainers.forEach(container => {
      if (!container._enhancedControls) {
        container._enhancedControls = new EnhancedVideoControls(container);
      }
    });
  }

  function scheduleInit(delay = 150) {
    setTimeout(initEnhancedControls, delay);
  }

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initEnhancedControls();
      scheduleInit(400);
    });
  } else {
    initEnhancedControls();
    scheduleInit(400);
  }

  // Re-initialize for dynamically loaded content (Shopify sections)
  document.addEventListener('shopify:section:load', () => {
    initEnhancedControls();
    scheduleInit(400);
  });

  // Ensure controls bind after video loads/plays
  document.addEventListener('video:play', () => scheduleInit(50));

  // Expose for manual initialization
  window.EnhancedVideoControls = EnhancedVideoControls;
  window.initEnhancedVideoControls = initEnhancedControls;

})();
